import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import { ArrowRight, Monitor } from "lucide-react";
import React, { useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import axiosInstanceAI from "@/api/axiosInstanceAI";
import axiosInstanceNode from "@/api/axiosInstanceNode";
import { useMainStore } from "@/stores";
import * as routeHelpers from "@/shared/utils/helperFunctions";
import { showError } from "@/shared/utils/toast";
import {
  // dismissWebsiteAnalysisToast,
  openWebsiteAnalysisToast,
  patchWebsiteAnalysisToast,
} from "@/features/onboarding/utils/websiteAnalysisToast";
import {
  brandLabelFromHost,
  hostnameFromUrl,
  normalizeWebsiteUrl,
} from "@/shared/utils/onboardingConfig";
import { newUserWebsiteUrlSchema } from "@/schema/OnBoarding";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";
import { clearOnboardingLlmDraft } from "@/features/onboarding/utils/onboardingLlmDraftStorage";

const cn = (...inputs) => twMerge(clsx(inputs));

/** Two pre-summary delays for org-admin analysis toast (~25s total). */
const WEBSITE_PHASE_MS_ORG_ADMIN = 12_500;
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

const FETCH_COMPANY_DATA_ERROR =
  "An error occurred while fetching company data. You can add details in settings.";

const WebsiteUrl = () => {
  const user = useMainStore((s) => s.user);
  const appMode = useMainStore((s) => s.appMode ?? "user");
  const setCompanyProfile = useMainStore((s) => s.setCompanyProfile);
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "all",
    resolver: zodResolver(newUserWebsiteUrlSchema),
    defaultValues: { website_url: "" },
  });

  const runAnalysis = useCallback(
    async (data) => {
      const normalized = normalizeWebsiteUrl(data.website_url);
      if (!normalized) {
        showError("Please enter your company website URL.");
        return;
      }
      const userId = user?.id;
      if (!userId) {
        showError("Missing user session. Please sign in again.");
        return;
      }

      const host = hostnameFromUrl(normalized);
      const brandLabel = brandLabelFromHost(host);

      openWebsiteAnalysisToast({
        brandLabel,
        hostScan: host || normalized.replace(/^https?:\/\//i, "").split("/")[0],
        patch: {
          headerSubtitle: "Reading your website...",
          phases: ["active", "pending", "pending"],
          progress: 12,
        },
      });

      setCompanyProfile({ website_url: normalized });

      const summaryPromise = axiosInstanceAI.post(
        "/website-summary/",
        {
          website_url: normalized,
        },
        { timeout: 120_000 },
      );
      const summarySettledPromise = summaryPromise.then(
        () => true,
        () => true,
      );

      await Promise.race([
        delay(WEBSITE_PHASE_MS_ORG_ADMIN),
        summarySettledPromise,
      ]);
      patchWebsiteAnalysisToast({
        phases: ["complete", "active", "pending"],
        progress: 36,
        headerSubtitle: "Fetching your services...",
      });

      await Promise.race([
        delay(WEBSITE_PHASE_MS_ORG_ADMIN),
        summarySettledPromise,
      ]);
      patchWebsiteAnalysisToast({
        phases: ["complete", "complete", "active"],
        progress: 62,
        headerSubtitle: "Creating summary...",
      });

      let response;
      try {
        response = await summaryPromise;
      } catch (analysisErr) {
        console.error(analysisErr);
      }

      const extractedServices = response?.data?.company_services;
      if (
        !response?.data?.success ||
        !response.data.summary ||
        !Array.isArray(extractedServices) ||
        extractedServices.length === 0
      ) {
        setCompanyProfile({ website_url: normalized });
        patchWebsiteAnalysisToast({
          phases: ["complete", "complete", "complete"],
          progress: 100,
          isDone: false,
          isError: true,
          errorMessage:
            response?.data?.error_message || FETCH_COMPANY_DATA_ERROR,
        });
        return;
      }

      setCompanyProfile({
        website_url: normalized,
        description: response.data.summary,
        services: extractedServices,
        company_services: extractedServices,
      });
      patchWebsiteAnalysisToast({
        phases: ["complete", "complete", "complete"],
        progress: 78,
        headerSubtitle: "Saving your profile...",
      });

      try {
        await axiosInstanceNode.post(
          "/auth/website-data",
          { ...useMainStore.getState().getCompanySubmitData() },
          { skipAuthRedirect: true },
        );
        clearOnboardingLlmDraft();
        const { setUser: updateUser, user: latestUser } =
          useMainStore.getState();
        updateUser({
          ...latestUser,
          isOnboarded: true,
          pricingPageCompleted: true,
        });
        patchWebsiteAnalysisToast({
          phases: ["complete", "complete", "complete"],
          progress: 100,
          headerSubtitle: "Company profile fetched successfully",
          isDone: true,
          isError: false,
          errorMessage: "",
        });
        navigate(
          routeHelpers.getTargetRoute(useMainStore.getState().user, {
            appMode,
          }),
          { replace: true },
        );
      } catch (err) {
        console.error(err);
        const serverDetail =
          err.response?.data?.error_message ||
          err.response?.data?.error ||
          err.message;
        patchWebsiteAnalysisToast({
          phases: ["complete", "complete", "complete"],
          progress: 100,
          isDone: false,
          isError: true,
          errorMessage: serverDetail
            ? `An error occurred while saving your company data: ${serverDetail}. Please try again.`
            : "An error occurred while saving your company data. Please try again.",
        });
      }
    },
    [user?.id, appMode, setCompanyProfile, navigate],
  );
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-gray-800 font-bold text-2xl">
          Lets start with your company website
        </h2>
        <p className="text-gray-500 text-sm">
          Paste your website URL below, we’ll pull your company details
        </p>
      </div>

      <div className="flex flex-col gap-5 w-full">
        <div className="flex flex-col gap-3 w-full">
          <div className="flex gap-2 items-center">
            <Monitor className="text-secondary" size={20} />
            <h4 className="text-gray-800 font-medium ">Company Website</h4>
          </div>
          <Controller
            name="website_url"
            control={control}
            render={({ field }) => (
              <CommonInput
                ref={field.ref}
                name={field.name}
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="Enter your website URL (e.g. example.com)"
                type="text"
                className={cn(
                  "w-full py-3.5 px-6 bg-white rounded-xl",
                  errors.website_url
                    ? "!border-red-500 focus-within:!border-red-500"
                    : "border-gray-300",
                )}
              />
            )}
          />
          {errors.website_url && (
            <p className="text-red-500 text-xs mt-0.5">
              {errors.website_url.message}
            </p>
          )}
        </div>
        <PrimaryButton
          startContent={false}
          endContent={<ArrowRight size={22} color="#fff" />}
          className="!h-auto !min-h-0 !py-[9px] !px-6"
          onClick={handleSubmit(runAnalysis)}
          disabled={isSubmitting}
          isLoading={isSubmitting}
        >
          Continue
        </PrimaryButton>
      </div>
    </div>
  );
};

export default WebsiteUrl;
